import {ApolloQueryResult} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React, {useState} from 'react';

import {archiveAnalysis, unarchiveAnalysis} from 'analysis-tool/AnalysisActions';
import {showAnalysisProgressTrayTask} from 'analysis-tool/progress/AnalysisProgressTrayTask';
import ArchiveModal, {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import * as AutomationPipelinesDAO from 'lab-automation/automation-pipelines/AutomationPipelinesDAO';
import {
  GetAnalysesForAssayRun,
  GetAnalysesForAssayRunVariables,
} from 'results/analysis-integration/__generated/GetAnalysesForAssayRun';
import {
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode_configVariables_AnalysisTemplateVariableNode as AnalysisTemplateVariableNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {GetAssayRunAnalysisIds} from 'results/analysis-integration/__generated/GetAssayRunAnalysisIds';
import {
  GET_ANALYSES_FOR_ASSAY_RUN_QUERY,
  isVariableMulti,
} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {AssayGroupProps} from 'runs/assay-group/AssayGroup';
import {OverflowDropdown} from 'runs/assay-group/AssayGroupEditButtons';
import AssayRunFields from 'runs/assay-group/AssayRunFields';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import SearchInput from 'search/inputs/SearchInput';
import {Filter} from 'search/SearchConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import AnalysisChip from 'ui-library/chips/AnalysisChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import {SaveState, SaveStatusIndicator} from 'ui-library/SaveStatusIndicator';
import * as TaskRunner from 'util/TaskRunner';

import SelectPipelineFilesModal from './SelectPipelineFilesModal';
import ViewAnalysisInputsModal from './ViewAnalysisInputsModal';

export const GET_FILE_NAME_QUERY = gql`
  query GetFileNameQuery($fileIds: [StrictString!]!) @skipAudit @engComponent(name: PIPELINES) {
    files(ids: $fileIds) {
      ... on FileNode {
        __typename
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface FileVariableBinding {
  variableId: string;
  variableValue: string | Array<string> | null;
}

/** @package */
export interface ExpandedState {
  inputIsExpanded: boolean | null;
  outputIsExpanded: boolean | null;
}
/**
 * @package
 * State of input/output sections in the run part.
 *
 * Input defaults to expanded and output defaults to null (i.e. collapsed).
 */
export const integrationExpandedGlobalClientState = makeUseGlobalClientState<ExpandedState>({
  inputIsExpanded: true,
  outputIsExpanded: null,
});

export const fileVariableBindingsGlobalClientState =
  makeUseGlobalClientState<Array<FileVariableBinding> | null>(null);

/** @package */
export function isVariableRequired(configVariable: AnalysisTemplateVariableNode): boolean {
  // Variable is considered required if it fulfills both conditions:
  // 1. Explicitly marked required
  // 2. If it's an isMulti variable, then minItems is defined and minItems > 0
  // The variable is not considered required variable.required === true but minItems === 0, because the lack
  // of a minimum implies that the field doesn't actually need to be populated.
  return (
    configVariable.variableTypeConfigSpec.required &&
    (configVariable.variableTypeConfigSpec.kind !== 'array' ||
      (configVariable.variableTypeConfigSpec.minItems != null &&
        configVariable.variableTypeConfigSpec.minItems > 0))
  );
}

export interface RunAnalysisAutomationParams {
  runApiId: string;
  fileVariableBindings: Array<FileVariableBinding>;
  previousAnalysisIds: Array<string>;
  onSaveRunFields: () => Promise<void>;
  refetchAnalysisIds: () => Promise<ApolloQueryResult<GetAssayRunAnalysisIds>>;
}

export async function runAnalysisAutomation(params: RunAnalysisAutomationParams): Promise<void> {
  const {runApiId, fileVariableBindings, previousAnalysisIds, onSaveRunFields, refetchAnalysisIds} = params;

  // Save the run schema fields first
  await onSaveRunFields();

  // Then run the analysis template
  const taskInfo = await AutomationPipelinesDAO.createAnalysisFromRun(runApiId, fileVariableBindings);
  await TaskRunner.resumeTaskPromise(taskInfo.status_url);

  // Refetch the IDs of analyses created from the run
  const refetchResult = await refetchAnalysisIds();

  // Find and show progress for any new analyses
  let newAnalysisIds: Array<string> = [];
  if (refetchResult.data?.run && !isInaccessible(refetchResult.data.run)) {
    newAnalysisIds = refetchResult.data.run.analysisIds || [];

    const addedAnalysisIds = newAnalysisIds.filter((id: string) => !previousAnalysisIds.includes(id));
    addedAnalysisIds.forEach((analysisId: string) => {
      showAnalysisProgressTrayTask(analysisId);
    });
  }
}

interface AnalysisTemplateIntegrationRunToolbarProps {
  // OverflowDropdown props
  assayRun: AssayRun;
  assayRunSchema: AssayRunSchema;
  handleRemoveAssayRun: () => void;
  locked: boolean;
  // Analysis template integration-specific props
  canRunAutomation: boolean;
  onRunAutomation: () => void;
  isRunning: boolean;
  canSave: boolean;
  onSave: () => void;
  isSaving: boolean;
}

export function AnalysisTemplateIntegrationRunToolbar({
  assayRun,
  assayRunSchema,
  handleRemoveAssayRun,
  locked,
  canRunAutomation,
  onRunAutomation,
  isRunning,
  canSave,
  onSave,
  isSaving,
}: AnalysisTemplateIntegrationRunToolbarProps): JSX.Element {
  return (
    <div className={css(styles.toolbar)}>
      {/**
       * When the automation is run, both isSaving and isRunning may be true (since we save the fields before
       * running the automation. In this case, we prefer displaying "Running" over "Saving".
      ) */}
      {isRunning ? (
        <SaveStatusIndicator saveStatus={SaveState.SAVING} messageOverride="Running..." />
      ) : (
        isSaving && <SaveStatusIndicator saveStatus={SaveState.SAVING} />
      )}
      <Tooltip
        title="Fill out all required fields in order to execute automation"
        disabled={canRunAutomation}
      >
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          text="Execute automation"
          onClick={onRunAutomation}
          disabled={!canRunAutomation}
          growToFitContents={true}
        />
      </Tooltip>
      <Tooltip title={!canSave ? 'Run fields are already saved' : undefined} disabled={canSave}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          size={ButtonSize.SMALL}
          text="Save without executing"
          onClick={onSave}
          disabled={!canSave}
          growToFitContents={true}
        />
      </Tooltip>
      <OverflowDropdown
        allowArchive={!locked}
        allowCopyId={!locked}
        assayRun={assayRun}
        assayRunSchema={assayRunSchema}
        handleRemoveAssayRun={handleRemoveAssayRun}
        isAutomationRun={false}
      />
    </div>
  );
}

interface InputFileFieldProps {
  configVariable: AnalysisTemplateVariableNode;
  binding: FileVariableBinding;
  setBinding: (newVariableValue: string | Array<string> | null) => void;
  runId: string;
  defaultFilters?: Array<Filter>;
  isDisabled?: boolean;
}

function InputFileField({
  configVariable,
  binding,
  setBinding,
  runId,
  defaultFilters,
  isDisabled,
}: InputFileFieldProps): JSX.Element {
  const [isUploading, setIsUploading] = useState(false);

  const variableName =
    configVariable.name.length > 0 ? configVariable.name : `File input for ${configVariable.id}`;
  const isMulti = isVariableMulti(configVariable.variableTypeConfigSpec);

  const minItems = isMulti ? configVariable.variableTypeConfigSpec.minItems : null;
  const maxItems = isMulti ? configVariable.variableTypeConfigSpec.maxItems : null;
  let limitInformation: string | null = null;
  if (minItems != null && maxItems != null) {
    limitInformation = `Accepts ${minItems} to ${maxItems} files`;
  } else if (minItems != null) {
    limitInformation = `Accepts ${minItems} or more files`;
  } else if (maxItems != null) {
    limitInformation = `Accepts ${maxItems} or fewer files`;
  }

  // Handles type translation
  function onSelectFiles(ids: Array<string>): void {
    if (ids.length === 0) {
      setBinding(null);
    } else if (!isMulti) {
      setBinding(ids[0]);
    } else {
      setBinding(ids);
    }
  }

  const onUploadFiles = (
    files: Array<File>,
    // IDs of files that have already been selected. If isMulti=false, or the desired behavior is that
    // selectedFileIds is overwritten, then the caller can just pass in an empty list.
    selectedFileIds: Array<string>
  ): JQueryPromise<void> => {
    setIsUploading(true);
    return AutomationPipelinesDAO.createFilesFromUploads(runId, files, {
      onFilesUploaded: async (s3Locations: Array<string>) => {
        const response = await AutomationPipelinesDAO.bulkCreatePipelineFiles(runId, s3Locations);
        onSelectFiles([...selectedFileIds, ...response.fileIds]);
        setIsUploading(false);
      },
    });
  };

  const selectedFileIds: Array<string> =
    binding.variableValue == null
      ? []
      : Array.isArray(binding.variableValue)
        ? binding.variableValue
        : [binding.variableValue];
  const selectedFilesNode = isUploading ? (
    'Uploading...'
  ) : binding.variableValue == null || binding.variableValue.length === 0 ? (
    'No file selected'
  ) : (
    <SearchInput
      autoFocus={false}
      disableTyping={true} // Disabling typing restricts the user from adding files
      types={[ModelTypeConstants.FILE]}
      selected={selectedFileIds}
      onSelect={noop}
      onRemove={(fileId) => {
        if (fileId == null) {
          return;
        }
        onSelectFiles(selectedFileIds.filter((id) => id !== fileId));
      }}
      placeholder=""
      isMulti={isMulti}
    />
  );

  return (
    <div
      className={css(styles.fileFieldWrapper)}
      {...getTestProps({
        component: 'AnalysisTemplateIntegrationRunAssayGroupBody',
        element: 'InputFileField',
        key: variableName,
      })}
    >
      <div>
        <FieldLabel label={variableName} isRequired={isVariableRequired(configVariable)} />
        {limitInformation != null && (
          <HelpButton
            renderContent={() => limitInformation}
            iconClassName="u-mini-spacing-left"
            placement={FloatingPlacement.RIGHT}
            interaction={FloatingInteraction.HOVER}
          />
        )}
      </div>
      <div className={css(styles.fileSelectorWrapper)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          fullWidth={false}
          text={`Select file${isMulti ? 's' : ''}`}
          icon={isUploading ? <BenchlingIcon icon="refresh" className="glyphicon-spin" /> : undefined}
          onClick={
            isDisabled
              ? noop
              : () => {
                  legacyRunModal(SelectPipelineFilesModal, {
                    title: variableName,
                    fileIds:
                      binding.variableValue == null
                        ? []
                        : typeof binding.variableValue === 'string'
                          ? [binding.variableValue]
                          : binding.variableValue,
                    isMulti,
                    minItems: minItems ?? undefined,
                    maxItems: maxItems ?? undefined,
                    onSelectFiles,
                    onUploadFiles,
                    defaultFilters,
                    runId,
                  });
                }
          }
          disabled={isDisabled}
        />
        <div className={css(styles.selectedFileText)}>{selectedFilesNode}</div>
      </div>
    </div>
  );
}

interface InputFileFieldsProps {
  analysisTemplate: AnalysisTemplateNode;
  runId: string;
  fileVariableBindings: Array<FileVariableBinding>;
  setFileVariableBindings: (newVariableBindings: Array<FileVariableBinding> | null) => void;
  defaultFiltersMapping: {[configVariableId: string]: Array<Filter>} | null;
  isDisabled?: boolean;
}

/** @package */
export function InputFileFields({
  analysisTemplate,
  runId,
  fileVariableBindings,
  setFileVariableBindings,
  defaultFiltersMapping,
  isDisabled,
}: InputFileFieldsProps): JSX.Element {
  return (
    <>
      {fileVariableBindings.map((binding, i) => (
        <InputFileField
          configVariable={analysisTemplate.configVariables.find(({id}) => id === binding.variableId)!}
          binding={binding}
          setBinding={(newVariableValue: string | Array<string> | null) => {
            setFileVariableBindings(
              fileVariableBindings.map((otherBinding) =>
                binding.variableId === otherBinding.variableId
                  ? {...otherBinding, variableValue: newVariableValue}
                  : otherBinding
              )
            );
          }}
          runId={runId}
          key={binding.variableId}
          defaultFilters={
            defaultFiltersMapping != null && defaultFiltersMapping.hasOwnProperty(binding.variableId)
              ? defaultFiltersMapping[binding.variableId]
              : undefined
          }
          isDisabled={isDisabled}
        />
      ))}
    </>
  );
}

/** @package */
export type AnalysisTemplateIntegrationRunAssayGroupBodyProps = {
  // Props that are used by AssayRunFields
  visibleRunFields: Array<SchemaField>;
  // Additional analysis template integration run props
  analysisTemplate: AnalysisTemplateNode;
  expandedState: ExpandedState;
  setExpandedState: (newExpandedState: ExpandedState) => void;
  fileVariableBindings: Array<FileVariableBinding> | null;
  setFileVariableBindings: (newVariableBindings: Array<FileVariableBinding> | null) => void;
  analysisIds: Array<string> | null;
} & Pick<AssayGroupProps, 'assayRun' | 'assayRunInfo' | 'assayRunSchemaInfo' | 'cid'>;

export function AnalysisTemplateIntegrationRunAssayGroupBody({
  analysisTemplate,
  expandedState,
  setExpandedState,
  fileVariableBindings,
  setFileVariableBindings,
  analysisIds,
  ...props
}: AnalysisTemplateIntegrationRunAssayGroupBodyProps): JSX.Element {
  return (
    <>
      <div
        onClick={() => {
          setExpandedState({...expandedState, inputIsExpanded: !expandedState.inputIsExpanded});
        }}
        className={css(styles.sectionHeader)}
      >
        <span>Inputs</span>
        <BenchlingIcon
          icon={expandedState.inputIsExpanded ? 'chevron-down' : 'chevron-right'}
          className={css(styles.icon)}
        />
      </div>
      {expandedState.inputIsExpanded && (
        <>
          <AssayRunFields
            assayRun={props.assayRun}
            analysisTemplate={analysisTemplate}
            displayAnalysisTemplateIntegrationUI={true}
            liveFields={props.visibleRunFields}
            versionedFields={props.assayRunSchemaInfo.versionedFields}
            versionedTags={props.assayRunInfo.versionedTags}
            cid={props.cid}
            isEditable={true}
            adapterId={null}
            connectionSchemaId={null}
            versionedConnectionId={null}
            versionedEquipmentId={props.assayRunInfo.versionedEquipmentId}
            connectionStatus={null}
            hasAutomationFiles={false}
          />
          <InputFileFields
            analysisTemplate={analysisTemplate}
            runId={props.assayRun.apiId!}
            fileVariableBindings={fileVariableBindings ?? []}
            setFileVariableBindings={setFileVariableBindings}
            defaultFiltersMapping={
              props.assayRunSchemaInfo.assayRunSchema.analysisTemplateDefaultFiltersMapping
            }
          />
        </>
      )}
      <BenchlingQuery<GetAnalysesForAssayRun, GetAnalysesForAssayRunVariables>
        query={GET_ANALYSES_FOR_ASSAY_RUN_QUERY}
        variables={{
          analysisIds: analysisIds ?? [],
        }}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {(data, {refetch}) => {
          const accessibleAnalyses = data.analyses
            .filter((analysis) => !isInaccessible(analysis))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          return (
            accessibleAnalyses.length > 0 && ( // Don't show the output section at all if no analyses have been created from the run
              <>
                <hr />
                <div
                  onClick={() => {
                    setExpandedState({...expandedState, outputIsExpanded: !expandedState.outputIsExpanded});
                  }}
                  className={css(styles.sectionHeader)}
                >
                  <span>Outputs</span>
                  <BenchlingIcon
                    icon={expandedState.outputIsExpanded ? 'chevron-down' : 'chevron-right'}
                    className={css(styles.icon)}
                  />
                </div>
                {expandedState.outputIsExpanded && (
                  <div className={css(styles.outputsSectionWrapper)}>
                    {accessibleAnalyses.map((analysis) => {
                      const analysisIsArchived = isArchived(analysis);
                      return (
                        <div key={analysis.id} className={css(styles.outputRow)}>
                          <AnalysisChip id={analysis.id} size={ChipSize.SMALL} />
                          <Dropdown
                            menu={
                              <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM} dropLeft={false}>
                                <Section>
                                  <Option
                                    onSelect={() => {
                                      legacyRunModal(ViewAnalysisInputsModal, {
                                        analysis,
                                        analysisTemplate,
                                      });
                                    }}
                                  >
                                    View inputs
                                  </Option>
                                  <Option
                                    onSelect={async () => {
                                      if (!analysisIsArchived) {
                                        legacyRunModal(ArchiveModal, {
                                          items: [analysis],
                                          onArchive: (
                                            _itemIds: Array<string>,
                                            purpose: string,
                                            options: ArchiveOptionsForModal
                                          ): void => {
                                            archiveAnalysis(
                                              analysis.id,
                                              purpose,
                                              options.resultPurpose ?? null
                                            );
                                          },
                                        });
                                      } else {
                                        await unarchiveAnalysis(analysis.id);
                                        refetch();
                                      }
                                    }}
                                  >
                                    {!analysisIsArchived ? 'Archive analysis' : 'Unarchive analysis'}
                                  </Option>
                                </Section>
                              </Menu>
                            }
                          >
                            <Button
                              buttonStyle={ButtonStyle.TERTIARY_GRAY}
                              icon="overflow"
                              size={ButtonSize.SMALL}
                            />
                          </Dropdown>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )
          );
        }}
      </BenchlingQuery>
    </>
  );
}

const styles = StyleSheet.create({
  fileFieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  fileSelectorWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  icon: {
    width: 12,
    height: 12,
  },
  outputsSectionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
  },
  outputRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoIcon: {
    width: 16,
    height: 16,
    cursor: 'pointer',
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
  selectedFileText: {
    ...FONT_PROXIMA_13,
    color: GRAY_SEMIDARK,
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
    cursor: 'pointer',
  },
  toolbar: {
    display: 'flex',
    gap: 8,
  },
});
